import { useEffect, useState, useCallback } from 'react'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: number
  message: string
  type: ToastType
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void
}

let toastCounter = 0
let addToastFn: ((message: string, type?: ToastType) => void) | null = null

export function showToast(message: string, type: ToastType = 'info') {
  if (addToastFn) {
    addToastFn(message, type)
  } else {
    alert(message)
  }
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastCounter
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 3500)
  }, [])

  useEffect(() => {
    addToastFn = addToast
    return () => {
      addToastFn = null
    }
  }, [addToast])

  if (toasts.length === 0) return null

  const typeStyles: Record<ToastType, string> = {
    success: 'bg-green-900/90 border-green-500 text-green-100',
    error: 'bg-red-900/90 border-red-500 text-red-100',
    info: 'bg-blue-900/90 border-blue-500 text-blue-100',
    warning: 'bg-yellow-900/90 border-yellow-500 text-yellow-100',
  }

  // Telegram Desktop / expanded Mini Apps cover the top of the webview with
  // chrome; keep toasts below safe + content insets (with a solid fallback).
  const topOffset =
    'max(3.75rem, calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px) + 0.75rem))'

  return (
    <div
      className="fixed left-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      style={{ top: topOffset }}
    >
      {toasts.map(t => (
        <div
          key={t.id}
          className={`pointer-events-auto backdrop-blur-md border rounded-xl px-4 py-3 shadow-2xl animate-slide-in ${typeStyles[t.type]}`}
        >
          <p className="text-sm font-medium text-center">{t.message}</p>
        </div>
      ))}
    </div>
  )
}